import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { dashboardAPI as apiConfig } from '../../../axiosConfig';
import { getEncodedURL } from '../../../utils/utils';

const URL = 'payment-options-proxy?endpoint=paymentdashboard';

export const getGateways = createAsyncThunk('gateways', async () => {
  const url = `${URL}/gateways`;
  const response = await apiConfig.get(url).catch((error) => {
    console.error(JSON.stringify(error));
    return error?.response;
  });

  response.data.details.sort((a, b) => a.id - b.id);

  return response.data;
});

export const getGatewayDetails = createAsyncThunk(
  'gateways/{gatewayId}',
  async (gatewayId) => {
    const url = `${URL}/gateways/${gatewayId}`;
    const response = await apiConfig.get(url).catch((error) => {
      console.error(JSON.stringify(error));
      return error?.response;
    });

    return response.data;
  },
);

export const getGatewayProperties = createAsyncThunk(
  'gateways/getGatewayProperties',
  async (payload) => {
    const url = getEncodedURL(`${URL}/merchantmethodproperties`, payload);

    const response = await apiConfig.get(url).catch((error) => {
      console.error(JSON.stringify(error));
      return error?.response;
    });

    return response.data;
  },
);

export const addNewGateway = createAsyncThunk(
  'gateways/{gatewayId}/addNew',
  async (payload, { getState }) => {
    // Check if a tab with this code already exists
    const existingGateway = getState().paymentOptions.gateways.gatewayList;
    const duplicateGateway = existingGateway.find(
      (gateway) => gateway.code === payload.code,
    );

    if (duplicateGateway) {
      throw new Error('A gateway with this code already exists');
    }

    const url = `${URL}/gateways`;
    const response = await apiConfig.post(url, payload).catch((error) => {
      console.error(JSON.stringify(error));
      return error?.response;
    });

    return response.data;
  },
);

export const updateGateway = createAsyncThunk(
  'gateways/{gatewayId}/update',
  async (payload) => {
    const url = `${URL}/gateways/${payload.id}`;
    const response = await apiConfig.put(url, payload).catch((error) => {
      console.error(JSON.stringify(error));
      return error?.response;
    });

    return response.data;
  },
);

export const updateGatewayMapping = createAsyncThunk(
  'gateways/updateGatewayMapping',
  async (payload) => {
    const url = `${URL}/merchantmethodmapping`;
    const response = await apiConfig.put(url, payload).catch((error) => {
      console.error(JSON.stringify(error));
      return error?.response;
    });

    return response.data;
  },
);

export const updateGatewayProperties = createAsyncThunk(
  'gateways/updateGatewayProperties',
  async (payload) => {
    const url = `${URL}/merchantmethodproperties`;
    const response = await apiConfig.put(url, payload).catch((error) => {
      console.error(JSON.stringify(error));
      return error?.response;
    });

    return response.data;
  },
);

export const deleteGatewayMapping = createAsyncThunk(
  'gateways/updateGatewayMapping',
  async (payload) => {
    const url = `${URL}/merchantmethodmapping`;
    const response = await apiConfig
      .delete(url, { data: payload })
      .catch((error) => {
        console.error(JSON.stringify(error));
        return error?.response;
      });

    return response.data;
  },
);

const paymentOptionsGatewaysSlice = createSlice({
  name: 'paymentOptionsGateways',
  initialState: {
    gatewayList: [],
    selectedGateway: {},
    selectedGatewayProperties: {},
  },
  reducers: {
    resetGateways(state) {
      state.gatewayList = [];
    },
    resetSelectedGateway(state) {
      state.selectedGateway = {};
    },
    resetSelectedProperties(state) {
      state.selectedGatewayProperties = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getGateways.fulfilled, (state, action) => {
        if (!action.payload?.details) {
          state.gatewayList = [];
          return;
        }
        state.gatewayList = action.payload.details;
      })
      .addCase(getGatewayDetails.fulfilled, (state, action) => {
        if (!action.payload?.id) {
          state.selectedGateway = {};
          return;
        }
        state.selectedGateway = action.payload;
      })
      .addCase(getGatewayProperties.fulfilled, (state, action) => {
        if (!action.payload?.properties) {
          state.selectedGatewayProperties = {};
          return;
        }
        state.selectedGatewayProperties = action.payload.properties;
      });
  },
});

export const selectGateways = (state) =>
  state.paymentOptions.gateways.gatewayList;

export const selectGatewayProperties = (state) =>
  state.paymentOptions.gateways.selectedGatewayProperties;

export const selectSelectedGateway = (state) =>
  state.paymentOptions.gateways.selectedGateway;

export const paymentOptionsGatewaysActions =
  paymentOptionsGatewaysSlice.actions;

export const selectPaymentGateways = (state) =>
  state.paymentOptions.gateways.gatewayList;

export default paymentOptionsGatewaysSlice.reducer;
